<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxConnectionAPI from 'dashboard/api/inboxConnection';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const POLL_INTERVAL_MS = 3000;
const QR_TIMEOUT_MS = 60000;

const view = ref('loading');
const data = ref(null);
const qr = ref(null);
const errorCode = ref(null);
const showDisconnectConfirm = ref(false);
const refreshingQr = ref(false);
const disconnecting = ref(false);

let pollTimer = null;
let pollDeadline = 0;

const friendlyPhone = computed(() => {
  if (!data.value?.phone_number) return null;
  const raw = String(data.value.phone_number);
  return raw.startsWith('+') ? raw : `+${raw}`;
});

const isConnected = computed(
  () => view.value === 'managed' && data.value?.connection_status === 'open'
);
const isConnecting = computed(
  () =>
    view.value === 'managed' && data.value?.connection_status === 'connecting'
);

const stopPolling = () => {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
};

const handleResponse = response => {
  const body = response?.data ?? {};
  if (body.state === 'managed') {
    data.value = body;
    view.value = 'managed';
  } else if (body.state === 'unmanaged') {
    view.value = 'unmanaged';
  } else if (body.state === 'unreachable') {
    view.value = 'unreachable';
  } else {
    view.value = 'error';
    errorCode.value = body.error || null;
  }
};

const handleError = error => {
  const status = error?.response?.status;
  const body = error?.response?.data;
  if (status === 503) {
    view.value = 'unreachable';
  } else {
    view.value = 'error';
    errorCode.value = body?.error || `http_${status || 'unknown'}`;
  }
};

const fetchStatus = async () => {
  try {
    const response = await InboxConnectionAPI.status(props.inbox.id);
    handleResponse(response);
  } catch (error) {
    handleError(error);
  }
};

const scheduleNextPoll = () => {
  stopPolling();
  if (Date.now() > pollDeadline) {
    // Timeout — keep QR visible but mark expired in subtitle.
    view.value = 'qr_expired';
    return;
  }
  pollTimer = setTimeout(async () => {
    try {
      const response = await InboxConnectionAPI.status(props.inbox.id);
      const body = response?.data ?? {};
      if (body.state === 'managed' && body.connection_status === 'open') {
        // Successfully paired — leave QR view, refresh status panel.
        qr.value = null;
        data.value = body;
        view.value = 'managed';
        return;
      }
      // Still pairing — keep polling.
      data.value = body.state === 'managed' ? body : data.value;
      scheduleNextPoll();
    } catch {
      scheduleNextPoll();
    }
  }, POLL_INTERVAL_MS);
};

const reconnect = async () => {
  refreshingQr.value = true;
  try {
    const response = await InboxConnectionAPI.refreshQr(props.inbox.id);
    const body = response?.data ?? {};
    if (body.state === 'qr' && body.qr_base64) {
      qr.value = {
        base64: body.qr_base64,
        expires_in: body.expires_in || 20,
      };
      view.value = 'qr';
      pollDeadline = Date.now() + QR_TIMEOUT_MS;
      scheduleNextPoll();
    } else if (body.state === 'qr' && !body.qr_base64) {
      // Evolution returned no QR — instance is already open. Refetch status.
      await fetchStatus();
    } else {
      handleResponse(response);
    }
  } catch (error) {
    handleError(error);
  } finally {
    refreshingQr.value = false;
  }
};

const askDisconnect = () => {
  showDisconnectConfirm.value = true;
};
const cancelDisconnect = () => {
  showDisconnectConfirm.value = false;
};
const confirmDisconnect = async () => {
  disconnecting.value = true;
  try {
    await InboxConnectionAPI.disconnect(props.inbox.id);
    showDisconnectConfirm.value = false;
    qr.value = null;
    await fetchStatus();
  } catch (error) {
    handleError(error);
  } finally {
    disconnecting.value = false;
  }
};

onMounted(fetchStatus);
onBeforeUnmount(stopPolling);
</script>

<template>
  <div class="px-8 py-6">
    <header class="mb-6 flex flex-col items-start gap-1.5">
      <h2
        class="text-n-slate-12 text-base font-semibold tracking-tight leading-tight m-0"
      >
        {{ t('INBOX_MGMT.CONNECTION.HEADING') }}
      </h2>
      <p class="text-n-slate-11 text-sm leading-relaxed m-0">
        {{ t('INBOX_MGMT.CONNECTION.SUBHEADING') }}
      </p>
    </header>

    <!-- LOADING -->
    <div
      v-if="view === 'loading'"
      class="bg-n-solid-1 dark:bg-n-solid-2 border border-n-weak os-r-xl os-elev-1 p-6"
    >
      <div class="flex items-center gap-2 text-sm text-n-slate-11">
        <span
          class="inline-block w-2 h-2 rounded-full bg-n-slate-7 animate-pulse"
          aria-hidden="true"
        />
        {{ t('INBOX_MGMT.CONNECTION.STATES.LOADING') }}
      </div>
    </div>

    <!-- MANAGED + CONNECTED -->
    <div
      v-else-if="isConnected && !qr"
      class="bg-n-solid-1 dark:bg-n-solid-2 border border-n-weak os-r-xl os-elev-1 p-6 os-tx-soft"
    >
      <div class="flex items-center gap-2 mb-5">
        <span
          class="inline-block w-2 h-2 rounded-full bg-n-brand"
          aria-hidden="true"
        />
        <span class="text-sm font-medium text-n-slate-12">{{
          t('INBOX_MGMT.CONNECTION.STATUS.CONNECTED')
        }}</span>
      </div>

      <dl class="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8">
        <div v-if="data?.profile_name">
          <dt class="text-xs uppercase tracking-wide text-n-slate-10">
            {{ t('INBOX_MGMT.CONNECTION.FIELDS.PROFILE_NAME') }}
          </dt>
          <dd class="text-sm text-n-slate-12 mt-0.5">
            {{ data.profile_name }}
          </dd>
        </div>
        <div v-if="friendlyPhone">
          <dt class="text-xs uppercase tracking-wide text-n-slate-10">
            {{ t('INBOX_MGMT.CONNECTION.FIELDS.PHONE') }}
          </dt>
          <dd class="text-sm text-n-slate-12 mt-0.5">{{ friendlyPhone }}</dd>
        </div>
        <div v-if="data?.integration">
          <dt class="text-xs uppercase tracking-wide text-n-slate-10">
            {{ t('INBOX_MGMT.CONNECTION.FIELDS.INTEGRATION') }}
          </dt>
          <dd class="text-sm text-n-slate-12 mt-0.5">{{ data.integration }}</dd>
        </div>
        <div v-if="data?.instance_name">
          <dt class="text-xs uppercase tracking-wide text-n-slate-10">
            {{ t('INBOX_MGMT.CONNECTION.FIELDS.INSTANCE') }}
          </dt>
          <dd class="text-sm text-n-slate-12 mt-0.5">
            {{ data.instance_name }}
          </dd>
        </div>
      </dl>

      <div class="mt-6 pt-5 border-t border-n-weak flex items-center gap-3">
        <template v-if="!showDisconnectConfirm">
          <NextButton
            ghost
            ruby
            :label="t('INBOX_MGMT.CONNECTION.ACTIONS.DISCONNECT')"
            @click="askDisconnect"
          />
        </template>
        <template v-else>
          <span class="text-sm text-n-slate-11">
            {{ t('INBOX_MGMT.CONNECTION.DISCONNECT_CONFIRM') }}
          </span>
          <NextButton
            ruby
            :is-loading="disconnecting"
            :label="t('INBOX_MGMT.CONNECTION.ACTIONS.CONFIRM_DISCONNECT')"
            @click="confirmDisconnect"
          />
          <NextButton
            ghost
            slate
            :label="t('INBOX_MGMT.CONNECTION.ACTIONS.CANCEL')"
            @click="cancelDisconnect"
          />
        </template>
      </div>
    </div>

    <!-- MANAGED + DISCONNECTED -->
    <div
      v-else-if="view === 'managed' && !isConnected && !qr"
      class="bg-n-solid-1 dark:bg-n-solid-2 border border-n-weak os-r-xl os-elev-1 p-6"
    >
      <div class="flex items-center gap-2 mb-3">
        <span
          class="inline-block w-2 h-2 rounded-full bg-n-slate-9"
          aria-hidden="true"
        />
        <span class="text-sm font-medium text-n-slate-12">{{
          isConnecting
            ? t('INBOX_MGMT.CONNECTION.STATUS.CONNECTING')
            : t('INBOX_MGMT.CONNECTION.STATUS.DISCONNECTED')
        }}</span>
      </div>
      <p class="text-sm text-n-slate-11 mb-5">
        {{ t('INBOX_MGMT.CONNECTION.DISCONNECTED_DESCRIPTION') }}
      </p>
      <NextButton
        :is-loading="refreshingQr"
        :label="t('INBOX_MGMT.CONNECTION.ACTIONS.RECONNECT')"
        @click="reconnect"
      />
    </div>

    <!-- QR PAIRING -->
    <div
      v-else-if="qr && (view === 'qr' || view === 'qr_expired')"
      class="bg-n-solid-1 dark:bg-n-solid-2 border border-n-weak os-r-xl os-elev-1 p-6"
    >
      <div class="grid md:grid-cols-[auto_1fr] gap-6 items-start">
        <div
          class="bg-white p-3 os-r-md border border-n-weak"
          style="width: 232px; height: 232px"
        >
          <img
            :src="qr.base64"
            :alt="t('INBOX_MGMT.CONNECTION.QR_ALT')"
            class="w-full h-full"
          />
        </div>
        <div>
          <h3 class="text-sm font-semibold tracking-tight text-n-slate-12 m-0">
            {{ t('INBOX_MGMT.CONNECTION.QR_TITLE') }}
          </h3>
          <ol
            class="text-sm text-n-slate-11 list-decimal pl-5 mt-3 space-y-1.5"
          >
            <li>{{ t('INBOX_MGMT.CONNECTION.QR_STEPS.OPEN') }}</li>
            <li>{{ t('INBOX_MGMT.CONNECTION.QR_STEPS.SETTINGS') }}</li>
            <li>{{ t('INBOX_MGMT.CONNECTION.QR_STEPS.LINK') }}</li>
          </ol>
          <div class="mt-5 flex items-center gap-3">
            <span class="text-xs text-n-slate-10">
              {{
                view === 'qr_expired'
                  ? t('INBOX_MGMT.CONNECTION.QR_EXPIRED')
                  : t('INBOX_MGMT.CONNECTION.QR_WAITING')
              }}
            </span>
            <NextButton
              ghost
              slate
              :is-loading="refreshingQr"
              :label="t('INBOX_MGMT.CONNECTION.ACTIONS.REFRESH_QR')"
              @click="reconnect"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- UNMANAGED -->
    <div
      v-else-if="view === 'unmanaged'"
      class="border border-dashed border-n-weak os-r-xl bg-n-alpha-2/40 p-8 text-center"
    >
      <p class="text-sm text-n-slate-12 font-medium">
        {{ t('INBOX_MGMT.CONNECTION.UNMANAGED_TITLE') }}
      </p>
      <p class="text-sm text-n-slate-11 mt-1.5 max-w-md mx-auto">
        {{ t('INBOX_MGMT.CONNECTION.UNMANAGED_DESCRIPTION') }}
      </p>
    </div>

    <!-- UNREACHABLE -->
    <div
      v-else-if="view === 'unreachable'"
      class="bg-n-solid-1 dark:bg-n-solid-2 border border-n-weak os-r-xl os-elev-1 p-6"
    >
      <div class="flex items-center gap-2 mb-2">
        <span
          class="inline-block w-2 h-2 rounded-full bg-n-amber-9"
          aria-hidden="true"
        />
        <span class="text-sm font-medium text-n-slate-12">{{
          t('INBOX_MGMT.CONNECTION.STATUS.UNREACHABLE')
        }}</span>
      </div>
      <p class="text-sm text-n-slate-11 mb-5">
        {{ t('INBOX_MGMT.CONNECTION.UNREACHABLE_DESCRIPTION') }}
      </p>
      <NextButton
        ghost
        slate
        :label="t('INBOX_MGMT.CONNECTION.ACTIONS.RETRY')"
        @click="fetchStatus"
      />
    </div>

    <!-- ERROR -->
    <div
      v-else
      class="bg-n-solid-1 dark:bg-n-solid-2 border border-n-weak os-r-xl os-elev-1 p-6"
    >
      <div class="flex items-center gap-2 mb-2">
        <span
          class="inline-block w-2 h-2 rounded-full bg-n-ruby-9"
          aria-hidden="true"
        />
        <span class="text-sm font-medium text-n-slate-12">{{
          t('INBOX_MGMT.CONNECTION.STATUS.ERROR')
        }}</span>
      </div>
      <p class="text-sm text-n-slate-11 mb-5">
        {{ t('INBOX_MGMT.CONNECTION.ERROR_DESCRIPTION') }}
        <span v-if="errorCode" class="text-n-slate-10 ml-1"
          >({{ errorCode }})</span
        >
      </p>
      <NextButton
        ghost
        slate
        :label="t('INBOX_MGMT.CONNECTION.ACTIONS.RETRY')"
        @click="fetchStatus"
      />
    </div>
  </div>
</template>
